import * as PIXI from "pixi.js";
import anime from "animejs";
import { symbolData } from "../../../SymbolData";
import { formatToCustomLocale } from "../../../utilities";
import { Settings } from "../../../settings";
import {
  COIN_SYMBOL_IDS,
  BONUS_SYMBOL_IDS,
  ROYAL_SYMBOL_ID,
  isCoinSymbol,
  isBonusSymbol,
  isJackpotSymbol,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import SpineAnimationData from "../../../utils/SpineAnimationData";
import { setSpinComplete, incrementWindupCount } from "../../../State/actions";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import store from "../../../State/Store/store";
import { isTurboLike, isSuperTurbo } from "../../../utils/speedHelpers";

// Dynamic sprite count formula: 1 top buffer + N visible + N landing zone + 1 bottom buffer
const SPRITE_COUNT = Settings.SYMBOLS_VISIBLE * 2 + 2;
const VISIBLE_SYMBOL_COUNT = Settings.SYMBOLS_VISIBLE;
// Visible symbols start after top buffer (index 1) + landing zone (N symbols)
const VISIBLE_SYMBOL_START_INDEX = 1 + Settings.SYMBOLS_VISIBLE;
// Stop animation moves sprites down by the landing zone distance (N symbol heights)
const STOP_MOVE_DISTANCE_SYMBOLS = Settings.SYMBOLS_VISIBLE;

// Cache available symbol IDs once (module-level constant for O(1) performance)
// Dynamically reads all symbols from symbolData that have a texture
// Includes: base symbols (0-9), zeus orbs (12-14)
// Excludes: r0 trigger (10), c0 scatter (11), c1 coin (15), bl blank (16),
//           jackpot coins (17-19), feature variants (20-25)
const AVAILABLE_SYMBOL_IDS = (() => {
  // Exclude non-base symbols from spin animation pool:
  // 10=r0, 11=c0, 12-14=zeus orbs (injected separately with controlled probability/spacing),
  // 15=coin, 16=blank, 17-19=jackpots, 20-25=feature variants
  const EXCLUDED_IDS = new Set([10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25]);
  return Object.entries(symbolData)
    .filter(([id, data]) => data?.texture && data.texture !== null && !EXCLUDED_IDS.has(parseInt(id, 10)))
    .map(([id]) => parseInt(id, 10));
})();

// Safari-specific precision factor (Safari doesn't need as much precision)
const PRECISION_FACTOR = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent,
)
  ? 10000
  : 100000;

// Coin value text Y offset derived from c.json spine's "value" bone (y=-82.67, skeleton h=338)
// In Spine Y-up coords, -82.67 means below center → in PixiJS Y-down, it's positive
const COIN_VALUE_Y_OFFSET = 82.67 / 338; // ≈ 0.2446 of symbolHeight

// Main game reel animation controller - handles wind-up, spin, stop animations with sprite wrapping
class ReelControllerV2 {
  constructor(
    dispatch,
    spinningContainer,
    spriteSheet,
    spriteSheetBlurred,
    // multiplierSpriteSheet, // TODO: Add multiplier support later
    reels,
    reelIndex,
    symbolWidth,
    symbolHeight,
    onReelStop = null, // Callback when reel stops (for landing animations)
    autoPlaySpeed = "auto", // Current autoplay speed mode
    bet = 1, // Current bet value for coin display
  ) {
    this.dispatch = dispatch;
    this.spinningContainer = spinningContainer;
    this.reelIndex = reelIndex;
    this.spritesheet = spriteSheet;
    this.spritesheetBlurred = FEATURES_CONFIG.SPIN.BLURRED_TEXTURES_ENABLED
      ? spriteSheetBlurred
      : null;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reels = reels || []; // Store API landing data (array of symbol IDs)
    this.hasPendingLandingSymbols = false; // Flag to indicate API data is ready
    this.onReelStop = onReelStop; // Callback for landing animations
    this.onReelLanded = null; // Callback when reel has fully landed (bounce complete, after onReelStop)
    this.onLandingPeak = null; // Callback when reel hits overshoot trough (lowest visual point, before bounce-back)
    this.autoPlaySpeed = autoPlaySpeed; // Current speed setting
    this.lockedSpinSpeed = null; // Locked speed for current spin (prevents mid-spin changes)
    this.bet = bet || 1; // Store bet value for coin calculations

    // Animation state
    this.isPlaying = false;
    this.hasStopped = false;
    this.isStopping = false;
    this._wasManualStopped = false; // True if this reel was force-stopped by manual stop
    this.isTensionMode = false;
    this.isLastNonTensionReel = false; // True when this reel is the last to stop before tension reels begin
    this.hasTensionThisSpin = false; // True when tension is active this spin (set by context, used by landing hook)
    this.tickerCallback = null;
    this.spinningSprites = [];
    this._spinElapsed = 0; // Accumulated spin time (ms) — advances only when ticker runs
    this.normalizedSpinSpeed = 0;
    this.lastCalculatedPosition = 0;
    this.pendingLandingSymbolId = null; // Deprecated: use this.reels array instead
    this.isBlurred = false;
    this.visibleSprites = [];
    this.nativeDimensions = null;
    this.hasInitializedTextures = false;
    this.stopTweenActive = false;
    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = symbolHeight;
    this._targetSymbolHeight = symbolHeight;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isWindingUp = false;
    this.windUpAnimation = null;
    this.speedMultiplier = 1.0;
    this.hasWinThisSpin = false; // Track if this spin has a win (affects sound playback)
    this.isHoldAndWinTrigger = false; // Track if this spin triggers Hold & Win (6+ coins)
    this.isExpandingWildActive = false; // Lock flag to prevent texture updates during expanding wild animation

    // Premium feel: acceleration, deceleration, velocity-based blur
    this.accelerationMultiplier = 1.0; // Ramps 0→1 during spin start
    this.currentVelocity = 0; // Effective velocity as fraction of max (0-1), used for blur
    this.isDecelerating = false; // True during phase 1 of 2-phase stop

    // Tension deceleration state (smooth slowdown when entering tension mode)
    this._tensionDecelerating = false; // True while reel is decelerating for tension
    this._tensionDecelElapsed = 0; // Accumulated tension deceleration time (ms)
    this._tensionDecelerationDuration = 0; // Computed at spinTension(): cycleDuration × DECELERATION_RATIO
    this._tensionSpeedMultiplier = 1.0; // Current speed multiplier (1.0 = full, ramps down to TARGET_SPEED)

    // Stop animation timing (accumulated frame time)
    this._stopElapsed = 0; // Accumulated stop animation time (ms)

    // Pause/resume: handled automatically by PIXI.Ticker.shared
    // When AppActivityContext sets ticker.speed = 0, deltaMS = 0
    // → all accumulated timers freeze in place → zero restoration code

    // Get symbol data from spritesheet
    this.reelSymbolsData =
      spriteSheet?.data?.frames || spriteSheet?.textures || null;

    // Internal caches for performance optimization
    this._speedConfigCache = null;
    this._lastLockedSpeed = null;
    this._lastAutoPlaySpeed = null;
    this._nativeDimensionsCache = new Map(); // Cache per symbol name
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;

    this._createSpinningSprites();
    this.spinningContainer.roundPixels = true;
  }

  // Picks a random symbol from available symbols (O(1) direct random)
  _pickRandomSymbolId() {
    if (!AVAILABLE_SYMBOL_IDS.length) {
      return 1; // fallback
    }
    return AVAILABLE_SYMBOL_IDS[
      Math.floor(Math.random() * AVAILABLE_SYMBOL_IDS.length)
    ];
  }

  // Calculates display value for coin symbols (coinValue * bet) with locale formatting
  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Use free rounds bet when active, otherwise use regular bet
      const state = store.getState();
      const freeRoundsActive = state?.freeRounds?.status === "active";
      const freeRoundsBet = state?.freeRounds?.betValue || null;
      const bet = freeRoundsActive && freeRoundsBet ? freeRoundsBet : this.bet;

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  // Receives new reel data from API - used for landing symbols when reel stops
  updateReels(newReels) {
    if (newReels && Array.isArray(newReels)) {
      this.reels = newReels;
      this.hasPendingLandingSymbols = true;
    }
  }

  // Updates bet value - coin texts will refresh naturally during spin when symbols wrap
  updateBet(newBet) {
    this.bet = newBet || 1;
    // Note: Coin texts update automatically when symbols wrap during spin via _setTexture()
    // This prevents immediate texture updates and maintains smooth gameplay
  }

  // Refresh all visible coin value texts (used when bet changes during free rounds)
  _refreshVisibleCoinTexts() {
    if (!this.spinningSprites || this.spinningSprites.length === 0) return;

    // Refresh ALL spinning sprites (not just visibleSprites which may not be set yet)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.valueText || !spriteData.currentSymbolId)
        continue;

      const symbolInfo = symbolData[spriteData.currentSymbolId];
      if (!symbolInfo || !symbolInfo.value) continue;

      // Skip jackpot symbols — they don't display text values
      if (isJackpotSymbol(spriteData.currentSymbolId)) continue;

      // Recalculate coin display value with new bet
      const displayValue = this._getCoinDisplayValue(symbolInfo.value);
      if (displayValue && displayValue !== "") {
        spriteData.valueText.text = displayValue;
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.text = displayValue;
        }
      }
    }
  }

  // Updates texture for spinning sprites
  updateSymbolTexture(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo || this.spinningSprites.length === 0) return;

    const visibleSpriteIndex = this.spinningSprites.length - 1;
    this._setSpriteTexture(this.spinningSprites[visibleSpriteIndex], symbolId);
    this.symbolId = symbolId;
  }

  // Creates spinning sprites with wrapping
  _createSpinningSprites() {
    if (!this.spinningContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      const normalSprite = new PIXI.Sprite();
      normalSprite.anchor.set(0.5);
      normalSprite.name = "spinning_symbol_texture";
      normalSprite.alpha = 1;
      normalSprite.roundPixels = true;
      symbolContainer.addChild(normalSprite);

      const blurredSprite = new PIXI.Sprite();
      blurredSprite.anchor.set(0.5);
      blurredSprite.name = "spinning_symbol_texture_blurred";
      blurredSprite.alpha = 0;
      blurredSprite.roundPixels = true;
      symbolContainer.addChild(blurredSprite);

      let valueText = null;
      let valueTextBlurred = null;
      if (FEATURES_CONFIG.SPIN.COIN_VALUES_ON_REELS && COIN_SYMBOL_IDS.length > 0) {
        const fontSize =
          this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        valueText = new PIXI.BitmapText("", {
          fontName: "font_secondary_scaled",
          fontSize: fontSize,
        });
        valueText.anchor.set(0.5);
        valueText.name = "symbol_value";
        valueText.zIndex = 1;
        valueText.alpha = 0;
        valueText.roundPixels = true;
        symbolContainer.addChild(valueText);

        if (FEATURES_CONFIG.SPIN.BLURRED_TEXTURES_ENABLED && PIXI.BitmapFont.available["font_secondary_blurred"]) {
          valueTextBlurred = new PIXI.BitmapText("", {
            fontName: "font_secondary_blurred",
            fontSize: fontSize,
          });
          valueTextBlurred.anchor.set(0.5);
          valueTextBlurred.name = "symbol_value_blurred";
          valueTextBlurred.zIndex = 1;
          valueTextBlurred.alpha = 0;
          valueTextBlurred.roundPixels = true;
          symbolContainer.addChild(valueTextBlurred);
        }
      }

      this.spinningSprites.push({
        container: symbolContainer,
        normalSprite,
        blurredSprite,
        valueText,
        valueTextBlurred,
        currentSymbolId: null,
      });

      this.spinningContainer.addChild(symbolContainer);
    }

    this._updateSpinningSprites();
    this._hideBufferSprites();
    this.hasInitializedTextures = true;
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    this.visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);
  }

  // Calculates symbol scale with per-category overrides.
  // Single lookup in SYMBOL_SCALE_OVERRIDES (with DEFAULT fallback) replaces
  // the old padding + override + feature-skip system.
  _calculateSymbolScale(symbolName, containerWidth, containerHeight, symbolId) {
    if (!containerHeight) return 1;

    // Look up symbolPay for per-category scale
    const info = symbolId != null ? symbolData[symbolId] : null;
    const symbolPay = info?.symbolPay || '';
    const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[symbolPay]
      ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
      ?? 1;

    // Check cache
    const cacheKey = `scale_${containerWidth}_${containerHeight}_${symbolPay}`;
    if (this._nativeDimensionsCache.has(cacheKey)) {
      return this._nativeDimensionsCache.get(cacheKey);
    }

    const scale = (containerHeight / Settings.SYMBOL_HEIGHT) * categoryScale;

    // Cache result
    this._nativeDimensionsCache.set(cacheKey, scale);

    return scale;
  }

  // Sets sprite textures (normal/blurred) and coin value text
  _setSpriteTexture(spriteData, symbolId) {
    if (spriteData.currentSymbolId === symbolId) {
      return;
    }

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0.001;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    spriteData.currentSymbolId = symbolId;
    const symbolName = symbolInfo.name;
    const textureName = symbolInfo.texture;
    const symbolValue = symbolInfo.value;

    // If texture is null or empty, set empty textures and skip processing
    if (!textureName || textureName === null) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0.001;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    if (this.spritesheet && textureName) {
      const texture =
        this.spritesheet.textures?.[`${textureName}.png`] || PIXI.Texture.EMPTY;
      spriteData.normalSprite.texture = texture;
      spriteData.normalSprite.alpha = this.isBlurred ? 0.001 : 1;

      if (this.spritesheetBlurred && spriteData.blurredSprite) {
        const blurredTexture =
          this.spritesheetBlurred.textures?.[`${textureName}_blurred.png`] ||
          PIXI.Texture.EMPTY;
        spriteData.blurredSprite.texture = blurredTexture;
        spriteData.blurredSprite.alpha = this.isBlurred ? 1 : 0.001;
      }
    }

    if (symbolName) {
      const scale = this._calculateSymbolScale(
        symbolName,
        this.symbolWidth,
        this.symbolHeight,
        symbolId,
      );
      if (spriteData.normalSprite) {
        spriteData.normalSprite.scale.set(scale);
      }
      if (spriteData.blurredSprite) {
        // Blurred textures have same canvas size as normal (squash is baked into content).
        // Use same uniform scale so blurred sprites match normal sprite size.
        spriteData.blurredSprite.scale.set(scale);
      }
    }

    // Update coin value text — skip entirely when symbol has no value (most symbols during spin)
    // Also skip jackpot symbols (27-29) — they have a value for payout but display tier via spine skins, not text
    if (spriteData.valueText && symbolValue && !isJackpotSymbol(symbolId)) {
      const displayValue = this._getCoinDisplayValue(symbolValue);
      spriteData.valueText.text = displayValue;
      spriteData.valueText.alpha =
        displayValue !== "" ? (this.isBlurred ? 0 : 1) : 0;
      spriteData.valueText.y = this.symbolHeight * COIN_VALUE_Y_OFFSET;

      // Apply scaling logic if text has content
      if (displayValue !== "") {
        // Reset scale first
        spriteData.valueText.scale.set(1);

        // Force update to get accurate measurements
        spriteData.valueText.updateText();

        // Calculate available width (with padding)
        const coinWidth = this.symbolWidth;
        const paddingPercent = 0.12; // 12% padding on each side
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = spriteData.valueText.width;

        // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
        if (actualTextWidth > maxTextWidth) {
          const scale = maxTextWidth / actualTextWidth;
          spriteData.valueText.scale.set(scale);
        }
      }

      // Update blurred text with same value and scaling
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = displayValue;
        spriteData.valueTextBlurred.alpha =
          displayValue !== "" ? (this.isBlurred ? 1 : 0) : 0;
        spriteData.valueTextBlurred.y = this.symbolHeight * COIN_VALUE_Y_OFFSET;

        if (displayValue !== "") {
          spriteData.valueTextBlurred.scale.set(1);
          spriteData.valueTextBlurred.updateText();

          const coinWidth = this.symbolWidth;
          const paddingPercent = 0.1;
          const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

          const actualTextWidth = spriteData.valueTextBlurred.width;

          // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
          if (actualTextWidth > maxTextWidth) {
            const scale = maxTextWidth / actualTextWidth;
            spriteData.valueTextBlurred.scale.set(scale);
          }
        }
      }
    } else if (spriteData.valueText) {
      // Non-coin symbol: clear text without heavy processing
      spriteData.valueText.text = "";
      spriteData.valueText.alpha = 0;
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = "";
        spriteData.valueTextBlurred.alpha = 0;
      }
    }
  }

  // Checks if any coin exists within MIN_COIN_SPACING positions below the top sprite (index 0).
  // After wrapping, index 0 is the newest sprite at the top. Indices 1, 2, etc. are below it.
  // This ensures coins are spaced apart in the strip, preventing visual stacking.
  _hasCoinWithinSpacing(spacing) {
    const limit = Math.min(spacing, this.spinningSprites.length);
    for (let i = 1; i < limit; i++) {
      const id = this.spinningSprites[i]?.currentSymbolId;
      if (id && isCoinSymbol(id)) return true;
    }
    return false;
  }

  // Checks if any zeus orb exists within the given spacing positions
  _hasBonusWithinSpacing(spacing) {
    const limit = Math.min(spacing, this.spinningSprites.length);
    for (let i = 1; i < limit; i++) {
      const id = this.spinningSprites[i]?.currentSymbolId;
      if (id && isBonusSymbol(id)) return true;
    }
    return false;
  }

  // Generates random symbol for spin animation (cosmetic only — API controls landing).
  // Uses FEATURES_CONFIG.COIN_GENERATION to determine:
  //   - Which reels can show coins / royals
  //   - Probability per symbol position
  //   - Minimum spacing between coins
  _generateRandomSymbolId() {
    const coinConfig = FEATURES_CONFIG.COIN_GENERATION;

    // Zeus orbs — low probability with spacing, independent of COIN_GENERATION mode
    if (BONUS_SYMBOL_IDS.length > 0) {
      const zeusSpacing = 3; // At least 2 non-zeus symbols between orbs
      if (!this._hasBonusWithinSpacing(zeusSpacing) && Math.random() < 0.08) {
        return BONUS_SYMBOL_IDS[Math.floor(Math.random() * BONUS_SYMBOL_IDS.length)];
      }
    }

    // No coin generation configured — base symbols only
    if (!coinConfig || !coinConfig.MODE) {
      return this._pickRandomSymbolId();
    }

    // Royal symbol — only on configured reels
    if (
      ROYAL_SYMBOL_ID &&
      coinConfig.ROYAL_REELS?.includes(this.reelIndex) &&
      Math.random() < (coinConfig.ROYAL_PROBABILITY || 0)
    ) {
      return ROYAL_SYMBOL_ID;
    }

    // Coin symbols — only on configured reels, with spacing constraint
    if (
      COIN_SYMBOL_IDS.length > 0 &&
      coinConfig.COIN_REELS?.includes(this.reelIndex)
    ) {
      // Enforce minimum spacing between coins
      const spacing = coinConfig.MIN_COIN_SPACING || 1;
      if (spacing > 1 && this._hasCoinWithinSpacing(spacing)) {
        return this._pickRandomSymbolId();
      }

      if (Math.random() < (coinConfig.PROBABILITY || 0)) {
        return COIN_SYMBOL_IDS[
          Math.floor(Math.random() * COIN_SYMBOL_IDS.length)
        ];
      }
    }

    return this._pickRandomSymbolId();
  }

  // Initializes textures - visible sprites use API data, buffers use random symbols
  _updateSpinningSprites() {
    if (!this.spinningSprites.length) return;

    const shouldSkipSprite0 =
      this.pendingLandingSymbolId !== null && this._isLandingSpriteVisible();

    // Check if we have initial reels data from API
    const hasInitialReels =
      this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === 0 && shouldSkipSprite0) continue;

      let symbolId;

      if (
        hasInitialReels &&
        i >= VISIBLE_SYMBOL_START_INDEX &&
        i < VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
      ) {
        const reelIndex = i - VISIBLE_SYMBOL_START_INDEX;
        symbolId = this.reels[reelIndex];
      } else {
        symbolId = this._generateRandomSymbolId();
      }

      this._setSpriteTexture(this.spinningSprites[i], symbolId);
    }
  }

  _resetSpritePositionsForSpin() {
    if (!this.spinningSprites.length) return;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }
  }

  // Checks if landing zone sprites are visible - determines when to apply API landing symbols
  _isLandingSpriteVisible() {
    if (!this.spinningContainer || this.spinningSprites.length < SPRITE_COUNT) {
      return false;
    }

    const containerY = this.spinningContainer.y;
    const visibleTop = -this.symbolHeight / 2;
    const visibleBottom = this.symbolHeight / 2;
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    // Check if all landing zone sprites (1-4) are within visible bounds
    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) return false;

      const absoluteY = containerY + sprite.container.y;
      if (absoluteY < visibleTop || absoluteY > visibleBottom) {
        return false;
      }
    }
    return true;
  }

  // Applies API landing symbols to landing zone - sprites drop into visible area on stop
  _setLandingSymbolOnVisibleSprites(symbolId = null) {
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    if (this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          const apiIndex = i - 1;
          const symbolIdForThisPosition = this.reels[apiIndex];

          if (
            symbolIdForThisPosition !== undefined &&
            symbolData[symbolIdForThisPosition]
          ) {
            this._setSpriteTexture(
              this.spinningSprites[i],
              symbolIdForThisPosition,
            );
          }
        }
      }
    } else if (symbolId !== null) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          this._setSpriteTexture(this.spinningSprites[i], symbolId);
        }
      }
    }
  }

  // Start spin with wind-up animation
  spin() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // DON'T reset expanding wild flag here - let it persist until new API data arrives
    // This prevents blinking when spinning after expanding wild
    // Flag will be reset in updateReels() when new data arrives from API

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }

    this._showBufferSprites();
    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    if (FEATURES_CONFIG.SPIN.WIND_UP_DURATION === 0) {
      this._startSpinAnimation();
      return;
    }

    this.isWindingUp = true;

    // Wind-up distance and duration match the bounce overshoot exactly.
    // easeOutBack overshoots by 4s³/(27(s+1)²) of total landing distance.
    // Bounce portion takes 2s/(3(s+1)) fraction of total stop time.
    const speedConfig = this._getSpeedConfig();
    const s = speedConfig.BOUNCE_OVERSHOOT;
    const overshootFraction = (4 * s * s * s) / (27 * (s + 1) * (s + 1));
    const windUpDistanceMultiplier = FEATURES_CONFIG.SPIN.WIND_UP_DISTANCE_MULTIPLIER ?? 1.0;
    const windUpDistance =
      -(overshootFraction * STOP_MOVE_DISTANCE_SYMBOLS * windUpDistanceMultiplier) *
      this.symbolHeight;

    // Calculate bounce duration from the stop timing to match wind-up timing
    const spinVelocity =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    const totalStopDuration =
      (((s + 3) * STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight * 1000) /
        spinVelocity) *
      speedConfig.STOP_SLOWDOWN_FACTOR;
    const bounceFraction = (2 * s) / (3 * (s + 1));
    const windUpSpeedFactor = FEATURES_CONFIG.SPIN.WIND_UP_SPEED_FACTOR ?? 1.0;
    const windUpDuration =
      Math.round(totalStopDuration * bounceFraction * windUpSpeedFactor) ||
      FEATURES_CONFIG.SPIN.WIND_UP_DURATION;

    const currentY = this.spinningContainer.y;
    const originalY = 0;

    if (Math.abs(currentY) > 0.001) {
      this.spinningContainer.y = originalY;
    }

    // Wind-up: pull up, then release back to origin.
    // Spin starts in the complete callback — zero overlap between anime.js and Ticker rAF loops.
    this.windUpAnimation = anime({
      targets: this.spinningContainer,
      y: [
        {
          value: originalY + windUpDistance,
          duration: windUpDuration,
          easing: "easeOutCubic",
        },
        {
          value: originalY,
          duration: windUpDuration * 0.6,
          easing: "easeInCubic",
        },
      ],
      complete: () => {
        this.spinningContainer.y = originalY;
        this.windUpAnimation = null;

        // Mark wind-up as done BEFORE dispatching — manual stop checks this flag
        this.isWindingUp = false;

        // Start spin animation FIRST — highest priority, no frame delay
        this._startSpinAnimation();

        // Defer Redux dispatch to AFTER paint — setTimeout(0) allows React 18 to batch
        // queueMicrotask runs before paint, causing 5 separate React re-renders per frame
        setTimeout(() => {
          this.dispatch(incrementWindupCount());
        }, 0);
      },
    });
  }

  // Start spin without wind-up (used for quick mode)
  spinNoWindUp() {
    if (this.isPlaying || this.isWindingUp) return;

    this.clearAllTimeouts();
    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }

    this._showBufferSprites();
    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    // Jump straight to spinning — FIRST, before any dispatch
    this._startSpinAnimation();

    // Defer Redux dispatch to AFTER paint
    setTimeout(() => {
      this.dispatch(incrementWindupCount());
    }, 0);
  }

  // Start turbo spin (no wind-up, no animation, instant setup)
  spinTurbo() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // DON'T reset expanding wild flag here - let it persist until new API data arrives
    // This prevents blinking when spinning after expanding wild
    // Flag will be reset in updateReels() when new data arrives from API

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    this.hasStopped = false;
    this.isStopping = false;
    this._wasManualStopped = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this._spinElapsed = 0;

    this._switchToNormalTextures();

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    this._showBufferSprites();
    this.spinningContainer.y = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    this.isPlaying = true;
  }

  // Stop turbo spin (instant drop with bounce)
  stopSpinTurbo() {
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;

    this.isPlaying = false;
    this.stopTweenActive = true;

    // Stop the spin ticker — prevent competing rAF loops during drop animation
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Unblur all sprites so landing symbols are sharp during drop
    this._switchToNormalTextures();

    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const apiIndex = i - 1;
      const symbolId = this.reels[apiIndex];
      if (symbolId !== undefined) {
        this._setSpriteTexture(this.spinningSprites[i], symbolId);
      }
    }

    const dropDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const turboConfig = FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO;
    const dropDuration = turboConfig.DROP_DURATION || 300;
    const bounceOvershoot = turboConfig.BOUNCE_OVERSHOOT ?? 1.5; // Use nullish coalescing to allow 0

    let lastProgress = 0;
    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = dropDistance;

    this.activeStopAnimation = anime({
      targets: { progress: 0 },
      progress: dropDistance,
      duration: dropDuration,
      easing: function () {
        return function (t, overshoot = bounceOvershoot) {
          return --t * t * ((overshoot + 1) * t + overshoot) + 1;
        };
      },
      update: (anim) => {
        const currentProgress = anim.animations[0].currentValue;
        const delta = currentProgress - lastProgress;
        lastProgress = currentProgress;

        this.stopAnimationProgress = currentProgress / dropDistance;

        if (Math.abs(delta) < 0.0001) return;

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        // Use cached wrap threshold
        const wrapThreshold =
          this._wrapThresholdCache || SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      },
      complete: () => {
        this.activeStopAnimation = null;
        this.stopAnimationProgress = 0;

        // Apply deferred resize if pending
        if (this._pendingPositionUpdate) {
          const wasStopped = this.hasStopped;
          const wasStopTweenActive = this.stopTweenActive;
          this.stopTweenActive = false;
          this.activeStopAnimation = null;

          this._applyPendingResize();

          this.hasStopped = wasStopped;
          this.stopTweenActive = wasStopTweenActive;
        }

        // Rotate array: move last N sprites to front (single O(n) operation)
        const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE_SYMBOLS;
        const tail = this.spinningSprites.splice(cut);
        this.spinningSprites.unshift(...tail);

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y = i * this.symbolHeight;
        }

        // Set critical state flags immediately
        this.spinningContainer.y = 0;
        this.hasStopped = true;
        this.isStopping = false;
        this.stopTweenActive = false;
        this.lockedSpinSpeed = null;

        this._hideBufferSprites();

        // 🚀 FIRE LANDING ANIMATIONS IMMEDIATELY - highest priority for smoothest UX
        if (this.onReelStop) {
          this.onReelStop(this.reelIndex, this.reels);
        }

        // Play sound when animation completes — reel_stop once + special symbol sound
        // In tension mode, the last non-tension reel plays the consolidated sound
        // instead of waiting for reel 4 (which is in tension)
        if (this.reelIndex === Settings.REEL_COUNT - 1 || this.isLastNonTensionReel) {
          const specialSound = this._getTurboModeSound();
          if (specialSound && specialSound !== SOUND_NAME.reel_stop) {
            // Wild/scatter/coin present — play only the special sound, skip reel_stop
            soundManager.play(specialSound);
          } else {
            // No special symbols — play a single reel_stop
            soundManager.play(SOUND_NAME.reel_stop);
          }
        }

        if (this.reelIndex === Settings.REEL_COUNT - 1) {
          this.dispatch(setSpinComplete(true));
        }

        // Reset tension flag
        this.isLastNonTensionReel = false;
      },
    });
  }

  // Starts spin animation after wind-up completes
  _startSpinAnimation() {
    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this._wasManualStopped = false;
    this.isBlurred = false;
    this.stopTweenActive = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this.isDecelerating = false;
    this.accelerationMultiplier = 0; // Start from 0, ramp up
    this.currentVelocity = 0;

    // Reset manual stop sound flag on first reel spin start
    if (this.reelIndex === 0) {
      ReelControllerV2._manualStopSoundPlayed = false;
    }

    // Reset tension deceleration for new spin
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Start reel_spin sound when first reel begins actual spinning (after wind-up)
    // Skip in super_turbo — spin is too fast for a looping sound to be audible
    if (this.reelIndex === 0 && !isSuperTurbo(this.autoPlaySpeed)) {
      soundManager.play(SOUND_NAME.reel_spin);
    }

    const speedConfig = this._getSpeedConfig();
    // Clamp speed to safe minimum: at 60fps each frame is ~16.7ms,
    // so speed must be >= 17ms/symbol to keep movement <= 1 symbolHeight/frame
    const safeSpeedMs = Math.max(speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT, 17);
    this.normalizedSpinSpeed =
      (this.symbolHeight / safeSpeedMs) * 1000;
    this._spinElapsed = 0;
    this.lastCalculatedPosition = 0;

    // No timer-based blur — velocity-based blur handles it in updateSymbolsAbsolute()

    this.tickerCallback = () => {
      if (!this.isPlaying && !this.stopTweenActive) {
        return;
      }
      this.updateSymbolsAbsolute();
    };

    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  // Initiates stop sequence - cannot stop during wind-up
  stopSpin() {
    if (this.isWindingUp) {
      return;
    }

    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;
    this._wasManualStopped = !!store.getState().spin.manualStop;

    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
  }

  // Tension mode — gradually decelerates the reel to build anticipation.
  // The reel smoothly slows from full speed to TARGET_SPEED over a duration
  // derived from the tension spine cycle: cycleDuration × DECELERATION_RATIO.
  // Blur naturally fades out as velocity drops below BLUR_VELOCITY_THRESHOLD.
  spinTension() {
    if (!this.isPlaying || this.isStopping || this.hasStopped) return;
    this.isTensionMode = true;

    const tensionDecel = FEATURES_CONFIG.TENSION.REEL_DECELERATION;
    if (tensionDecel?.ENABLED) {
      // Derive deceleration duration from spine data × ratio (no hardcoded ms)
      const cycleDuration = SpineAnimationData.getTensionCycleDuration()
        || FEATURES_CONFIG.TENSION.DURATION; // Fallback to config if spine not cached yet
      this._tensionDecelerationDuration = cycleDuration * (tensionDecel.DECELERATION_RATIO ?? 0.45);
      this._tensionDecelerating = true;
      this._tensionDecelElapsed = 0;
      this._tensionSpeedMultiplier = 1.0; // Start at current speed, ramps down gradually
    }
  }

  // Stops tension mode spin — just triggers normal stop
  // Marks reel as tension so _finalizeLanding can play per-reel sounds (not consolidated turbo sound)
  stopSpinTension() {
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;
    this.isStopping = true;

    this.isTensionMode = true;
  }

  // Freeze spin instantly - stops all animations without landing
  // Used for error popups where reels should freeze at current position
  freezeSpin() {
    // Stop ticker immediately
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop wind-up if active
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this.spinningContainer); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
      this.isWindingUp = false;
    }

    // Clear ticker-based stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;

    // Set state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.speedMultiplier = 1.0;
    this._spinElapsed = 0;
    this.lockedSpinSpeed = null;
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;
    this.isDecelerating = false;

    // Reset tension deceleration state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Clear all pending timeouts
    this.clearAllTimeouts();

    // DO NOT call onReelStop - no landing animations
    // DO NOT dispatch setSpinComplete - popup handles flow
    // DO NOT switch textures - keep current blur state
  }

  // Pause/resume for tab switching — NO-OPS.
  // AppActivityContext sets PIXI.Ticker.shared.speed = 0 on tab hide,
  // which makes deltaMS = 0 for all subscribers. Since all timing now
  // accumulates deltaMS, everything freezes automatically.
  // anime.js wind-up/turbo-stop are rAF-based — also freeze when ticker pauses.
  // Methods kept for API compatibility with callers.
  pauseSpin() {}
  resumeSpin() {}


  // Restore reel to idle state with specific symbols
  // Used after error recovery to show previous round's symbols properly
  // @param {Array} symbolIds - Array of symbol IDs to restore
  // @param {number|null} betValue - Optional bet value to use for coin texts (for error recovery)
  restoreToIdleState(symbolIds, betValue = null) {
    // Clear all animations and timeouts
    this.clearAllTimeouts();

    // Stop ticker if active
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop any active animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this.spinningContainer); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
      this.isWindingUp = false;
    }

    // Clear ticker-based stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;

    // Reset state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this._spinElapsed = 0;
    this.lockedSpinSpeed = null;
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;
    this.isDecelerating = false;

    // Reset tension deceleration state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Switch to normal (non-blurred) textures
    this._switchToNormalTextures();

    // Ensure sprites exist
    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
    }

    // Reset container position to default
    this.spinningContainer.y = 0;

    // Position all sprites to their default positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Set visible sprites to show the provided symbols
    // Visible sprites are at indices VISIBLE_SYMBOL_START_INDEX to VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
    if (symbolIds && Array.isArray(symbolIds)) {
      // Store original bet and temporarily use historical bet if provided
      const originalBet = this.bet;
      if (betValue !== null && betValue !== undefined) {
        this.bet = betValue;
      }

      for (let i = 0; i < VISIBLE_SYMBOL_COUNT && i < symbolIds.length; i++) {
        const spriteIndex = VISIBLE_SYMBOL_START_INDEX + i;
        const symbolId = symbolIds[i];
        if (symbolId !== undefined && this.spinningSprites[spriteIndex]) {
          this._setSpriteTexture(this.spinningSprites[spriteIndex], symbolId);
        }
      }

      // Fill other sprites with random symbols for smooth next spin
      for (let i = 0; i < this.spinningSprites.length; i++) {
        if (
          i < VISIBLE_SYMBOL_START_INDEX ||
          i >= VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
        ) {
          const randomSymbolId = this._generateRandomSymbolId();
          this._setSpriteTexture(this.spinningSprites[i], randomSymbolId);
        }
      }

      // Update reels data for next spin
      this.reels = symbolIds;
      this.hasPendingLandingSymbols = false;

      // NOTE: Do NOT call this.onReelStop here.
      // onReelStop triggers full landing animations (including bolt trails)
      // which is wrong for error recovery — we just need the symbols placed.
      // The caller (ErrorRecoveryOverlay) triggers idle-only animations
      // via triggerInitialIdleAnimations() after all reels are restored.

      // Restore original bet after idle animations are triggered
      if (betValue !== null && betValue !== undefined) {
        this.bet = originalBet;
      }
    }

    this._hideBufferSprites();
  }

  // Toggle roundPixels on all sprites
  // Disable during tension zoom to prevent jitter from fractional container scaling
  // Re-enable after zoom completes for crisp 1:1 rendering
  setRoundPixels(enabled) {
    this.spinningContainer.roundPixels = enabled;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      sprite.container.roundPixels = enabled;
      sprite.normalSprite.roundPixels = enabled;
      sprite.blurredSprite.roundPixels = enabled;
      if (sprite.valueText) sprite.valueText.roundPixels = enabled;
      if (sprite.valueTextBlurred) sprite.valueTextBlurred.roundPixels = enabled;
    }
  }

  // Main animation loop
  updateSymbolsAbsolute() {
    if (this.hasStopped) {
      return;
    }

    // --- Ticker-based stop animation (replaces anime.js for seamless handoff) ---
    if (this.stopTweenActive && this._stopDuration > 0) {
      this._stopElapsed += PIXI.Ticker.shared.deltaMS;
      const elapsed = this._stopElapsed;
      const t = Math.min(elapsed / this._stopDuration, 1.0);

      // BackOut easing: f(t) = (t-1)² × ((s+1)(t-1) + s) + 1
      const s = this._stopOvershoot;
      const t1 = t - 1;
      const easedT = t1 * t1 * ((s + 1) * t1 + s) + 1;
      const easedPosition = this._stopDistance * easedT;
      const delta = easedPosition - this._stopLastEasedPos;
      this._stopLastEasedPos = easedPosition;

      this.stopAnimationProgress = easedPosition / this._stopDistance;

      // Update blur during stop (velocity decreases as easing decelerates)
      // BackOut derivative: f'(t) = 3(s+1)(t-1)² + 2s(t-1)
      const easingVelocity = 3 * (s + 1) * t1 * t1 + 2 * s * t1;
      this.currentVelocity = Math.abs(easingVelocity) * 0.3;
      this._updateBlurFromVelocity();

      if (Math.abs(delta) > 0.0001) {
        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        const wrapThreshold =
          this._wrapThresholdCache || SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      }

      // Detect overshoot peak (trough) — the exact frame where symbols reach their lowest point
      // BackOut peak: t_peak = 1 - 2s / (3(s+1)), only when s > 0 (has bounce)
      if (!this._landingPeakFired && this._stopOvershoot > 0) {
        const sPeak = this._stopOvershoot;
        const tPeakMath = 1 - (2 * sPeak) / (3 * (sPeak + 1));
        // LANDING_PEAK_OFFSET: 1.0 = exact trough, <1.0 = fires earlier
        const peakOffset = this._getSpeedConfig()?.LANDING_PEAK_OFFSET ?? 1.0;
        const tPeak = tPeakMath * peakOffset;
        if (t >= tPeak) {
          this._landingPeakFired = true;
          // Play only the reel_stop thud at impact — special symbol sounds stay in _finalizeLanding
          // Skip during turbo/manual stop — quick_stop plays once on first force-stopped reel
          const skipPeakSound = this._wasManualStopped || isTurboLike(this.autoPlaySpeed);
          if (!skipPeakSound) {
            soundManager.play(SOUND_NAME.reel_stop);
          }
          if (this.onLandingPeak) {
            this.onLandingPeak(this.reelIndex);
          }
        }
      }

      if (t >= 1.0) {
        this._finalizeLanding();
      }
      return;
    }

    // Accumulate elapsed time from ticker (freezes when tab hidden)
    this._spinElapsed += PIXI.Ticker.shared.deltaMS;
    const elapsedTime = this._spinElapsed / 1000;
    const currentExpectedPosition = elapsedTime * this.normalizedSpinSpeed;

    let movement = currentExpectedPosition - this.lastCalculatedPosition;
    this.lastCalculatedPosition = currentExpectedPosition; // Always track at full speed

    // --- Tension deceleration (smooth slowdown for anticipation) ---
    // Scales the per-frame visual movement while keeping position tracking at full speed.
    // This way the gap doesn't grow to compensate — the reel genuinely scrolls slower.
    if (this._tensionDecelerating) {
      const tensionDecel = FEATURES_CONFIG.TENSION.REEL_DECELERATION;
      this._tensionDecelElapsed += PIXI.Ticker.shared.deltaMS;
      const tensionElapsed = this._tensionDecelElapsed;
      const tDecel = Math.min(tensionElapsed / this._tensionDecelerationDuration, 1.0);
      // Ease-out cubic: starts slowing immediately, arrives at target speed gradually.
      // Produces a visible, dramatic deceleration from the very first frame.
      const t1 = 1 - tDecel;
      const easedDecel = 1 - (t1 * t1 * t1);
      this._tensionSpeedMultiplier = 1.0 - (1.0 - tensionDecel.TARGET_SPEED) * easedDecel;



      // Scale visual movement only — position tracker stays at full speed
      movement *= this._tensionSpeedMultiplier;
    }

    // --- Acceleration ramp-up (ease-out: fast start, smooth arrival at full speed) ---
    const speedConfig = this._getSpeedConfig();
    // Support ratio-based (ACCEL_SPEED_RATIO × SPEED_MS) or legacy absolute (ACCELERATION_DURATION)
    const accelDuration = speedConfig.ACCELERATION_DURATION
      ?? (speedConfig.ACCEL_SPEED_RATIO
        ? speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT * speedConfig.ACCEL_SPEED_RATIO
        : 0);
    if (accelDuration > 0 && this.accelerationMultiplier < 1.0) {
      const elapsedMs = this._spinElapsed;
      const t = Math.min(elapsedMs / accelDuration, 1.0);
      // Ease-out cubic: starts fast, smoothly reaches 1.0
      this.accelerationMultiplier = 1 - (1 - t) * (1 - t) * (1 - t);
    } else {
      this.accelerationMultiplier = 1.0;
    }

    // Apply acceleration multiplier
    movement *= this.accelerationMultiplier;

    // --- Blur: smooth per-frame velocity-based crossfade ---
    // During tension deceleration, velocity tracks the tension speed multiplier
    // → blur naturally fades out as speed drops below BLUR_VELOCITY_THRESHOLD
    this.currentVelocity = this._tensionDecelerating
      ? this._tensionSpeedMultiplier
      : this.accelerationMultiplier;
    this._updateBlurFromVelocity();

    // Cache wrap threshold calculation
    if (
      this._wrapThresholdCache === null ||
      this._lastSymbolHeightForWrapThreshold !== this.symbolHeight
    ) {
      this._wrapThresholdCache = SPRITE_COUNT * this.symbolHeight;
      this._lastSymbolHeightForWrapThreshold = this.symbolHeight;
    }
    const wrapThreshold = this._wrapThresholdCache;
    const shouldUpdateTexture = !this.isStopping && !this.hasStopped;

    if (
      this.pendingLandingSymbolId !== null &&
      !this._isLandingSpriteVisible()
    ) {
      this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
      this.pendingLandingSymbolId = null;
    }

    let shouldExecuteStop = false;

    // Phase 1: Apply movement to ALL sprites uniformly (no break-and-snap)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y += movement;
    }

    // Phase 2: Check for wraps AFTER all sprites have moved
    // While loop handles multiple wraps per frame at high speeds (safety for extreme SPEED_MS values)
    let wrapsThisFrame = 0;
    const lastIndex = this.spinningSprites.length - 1;
    while (
      this.spinningSprites[lastIndex].container.y >= wrapThreshold &&
      wrapsThisFrame < SPRITE_COUNT // Safety cap: can never need more wraps than sprites
    ) {
      wrapsThisFrame++;

      if (this._pendingPositionUpdate) {
        this._applyPendingResize();
      }

      this.moveLastSymbolOnTop();

      if (
        this.pendingLandingSymbolId !== null &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.pendingLandingSymbolId = null;
      }

      if (shouldUpdateTexture) {
        // Update texture on the sprite that just wrapped to top (now index 0)
        this.updateSymbolTextureRandomly(this.spinningSprites[0]);
      }

      if (this.isStopping && !this.hasStopped && !this.stopTweenActive) {
        shouldExecuteStop = true;
      }
    }

    if (shouldExecuteStop) {
      this._calculateAndExecuteStop();
    }
  }

  // Wraps last sprite to top — single-sprite teleport (no grid-snap on other sprites)
  moveLastSymbolOnTop() {
    const movedSymbol = this.spinningSprites.pop();
    this.spinningSprites.unshift(movedSymbol);

    // Teleport only the wrapped sprite to the top position.
    // Other sprites keep their exact Y positions — no grid-snap stutter.
    // The total strip height is the distance from top to wrap threshold.
    const totalStripHeight = SPRITE_COUNT * this.symbolHeight;
    movedSymbol.container.y -= totalStripHeight;
  }

  // Calculates current drift from expected positions
  _calculateCurrentDrift() {
    if (!this.spinningSprites.length) return 0;

    const visibleStartIndex = VISIBLE_SYMBOL_START_INDEX;
    const visibleEndIndex = visibleStartIndex + VISIBLE_SYMBOL_COUNT;

    let totalDrift = 0;
    let count = 0;

    for (
      let i = visibleStartIndex;
      i < visibleEndIndex && i < this.spinningSprites.length;
      i++
    ) {
      const expectedY = i * this.symbolHeight;
      const actualY = this.spinningSprites[i].container.y;
      const drift = actualY - expectedY;
      totalDrift += drift;
      count++;
    }

    const averageDrift = count > 0 ? totalDrift / count : 0;
    return averageDrift;
  }

  // Updates sprite with random symbol
  updateSymbolTextureRandomly(spriteData) {
    const randomSymbolId = this._generateRandomSymbolId();
    this._setSpriteTexture(spriteData, randomSymbolId);
  }

  // Initializes textures for spinning sprites
  _initializeSpinningTextures() {
    this.spinningSprites.forEach((spriteData, index) => {
      const randomSymbolId = this._generateRandomSymbolId();
      this._setSpriteTexture(spriteData, randomSymbolId);
    });
    this.hasInitializedTextures = true;
  }

  _getReelStopSound() {
    let hasWild = false;
    let hasScatter = false;
    let hasCoin = false;

    // Read from actual visible sprite positions — not this.reels which may include buffer data
    for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
      const spriteData = this.spinningSprites[VISIBLE_SYMBOL_START_INDEX + i];
      const symbolId = spriteData?.currentSymbolId;
      if (symbolId === undefined || symbolId === null) continue;

      const symInfo = symbolData[symbolId];
      if (!symInfo) continue;

      if (symInfo.symbolPay === "wild") {
        hasWild = true;
      } else if (symInfo.symbolPay === "scatter") {
        hasScatter = true;
      } else if (COIN_SYMBOL_IDS.includes(symbolId)) {
        hasCoin = true;
      }
    }

    if (hasScatter) return SOUND_NAME.scatter_1;
    if (hasWild) return SOUND_NAME.wild;
    if (hasCoin) return SOUND_NAME.coin;
    return SOUND_NAME.reel_stop;
  }

  // Determines turbo mode stop sound by checking all reels - used on last reel
  _getTurboModeSound() {
    const state = store.getState();
    if (
      !state ||
      !state.game ||
      !state.game.currentRound ||
      !state.game.currentRound.reels
    ) {
      return this._getReelStopSound();
    }

    const allReels = state.game.currentRound.reels;
    let hasWild = false;
    let hasScatter = false;
    let hasCoin = false;

    for (let reelIndex = 0; reelIndex < allReels.length; reelIndex++) {
      const reel = allReels[reelIndex];
      if (!reel) continue;

      for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
        const symbolId = reel[i];
        if (symbolId === undefined) continue;

        const symInfo = symbolData[symbolId];
        if (!symInfo) continue;

        if (symInfo.symbolPay === "wild") {
          hasWild = true;
          break;
        } else if (symInfo.symbolPay === "scatter") {
          hasScatter = true;
        } else if (COIN_SYMBOL_IDS.includes(symbolId)) {
          hasCoin = true;
        }
      }

      if (hasWild) break;
    }

    if (hasScatter) return SOUND_NAME.scatter_1;
    if (hasWild) return SOUND_NAME.wild;
    if (hasCoin) return SOUND_NAME.coin;
    return SOUND_NAME.reel_stop;
  }

  // Sets win state for this spin - affects sound playback
  setHasWinThisSpin(hasWin) {
    this.hasWinThisSpin = !!hasWin;
  }

  // Sets Hold & Win trigger state for this spin - affects spin complete delay
  setIsHoldAndWinTrigger(isTrigger) {
    this.isHoldAndWinTrigger = !!isTrigger;
  }

  // Gets speed configuration based on locked or current autoPlaySpeed
  // Cached for performance - invalidated when speed changes
  _getSpeedConfig() {
    const effectiveSpeed = this.lockedSpinSpeed || this.autoPlaySpeed;

    // Check cache validity
    if (
      this._speedConfigCache !== null &&
      this._lastLockedSpeed === this.lockedSpinSpeed &&
      this._lastAutoPlaySpeed === this.autoPlaySpeed
    ) {
      return this._speedConfigCache;
    }

    const config =
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[effectiveSpeed?.toUpperCase()] ||
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;

    // Cache result
    this._speedConfigCache = config;
    this._lastLockedSpeed = this.lockedSpinSpeed;
    this._lastAutoPlaySpeed = this.autoPlaySpeed;

    return config;
  }

  // Unblur all sprites - instant transition
  _switchToNormalTextures() {
    if (!this.spritesheetBlurred) {
      return Promise.resolve();
    }

    this.spinningSprites.forEach((sprite) => {
      if (!sprite || !sprite.blurredSprite) return;
      sprite.normalSprite.alpha = 1;
      sprite.blurredSprite.alpha = 0;

      // Switch text back to normal
      if (sprite.valueText && sprite.valueText.text !== "") {
        sprite.valueText.alpha = 1;
      }
      if (sprite.valueTextBlurred) {
        sprite.valueTextBlurred.alpha = 0;
      }
    });

    this.isBlurred = false;

    return Promise.resolve();
  }

  // Selectively unblurs landing symbols during stop animation
  _switchToNormalTexturesSelective() {
    if (!this.spritesheetBlurred) return;

    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
    const bufferAbove = landingZoneStart - 1;
    const bufferBelow = landingZoneEnd;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      const isLandingZone = i >= landingZoneStart && i < landingZoneEnd;
      const isBufferAbove = i === bufferAbove;
      const isBufferBelow = i === bufferBelow;

      if (isLandingZone || isBufferAbove || isBufferBelow) {
        spriteData.normalSprite.alpha = 1;
        spriteData.blurredSprite.alpha = 0;

        // Switch text to normal for landing symbols
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }
    }

    this.isBlurred = false;
  }

  // Proportional blur: sets alpha of normal/blurred sprites based on velocity
  // Below threshold = fully sharp, above = gradually blurred proportional to speed
  _updateBlurFromVelocity() {
    if (!this.spritesheetBlurred) return;

    const speedConfig = this._getSpeedConfig();
    const threshold = speedConfig.BLUR_VELOCITY_THRESHOLD || 0.6;
    const velocity = this.currentVelocity;

    // Calculate blur amount: 0 = fully sharp, 1 = fully blurred
    // Ramp from threshold to 1.0 (full speed)
    let blurAmount;
    if (velocity < threshold) {
      blurAmount = 0;
    } else {
      // Normalized 0-1 within the blur range, squared for natural feel
      const t = (velocity - threshold) / (1.0 - threshold);
      blurAmount = Math.min(t * t, 1.0);
    }

    // Update isBlurred state for cleanup/state tracking
    this.isBlurred = blurAmount > 0.01;

    // Apply alpha proportionally to all sprites
    const normalAlpha = 1 - blurAmount;
    const blurredAlpha = blurAmount;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      // During stop animation, skip landing zone + buffer sprites (already unblurred)
      if (this.stopTweenActive) {
        const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
        if (i >= 0 && i <= landingZoneEnd) continue;
      }

      spriteData.normalSprite.alpha = Math.max(normalAlpha, 0.001);
      spriteData.blurredSprite.alpha = blurredAlpha;

      // Blend coin value text alpha too
      if (spriteData.valueText && spriteData.valueText.text !== "") {
        spriteData.valueText.alpha = normalAlpha;
      }
      if (
        spriteData.valueTextBlurred &&
        spriteData.valueTextBlurred.text !== ""
      ) {
        spriteData.valueTextBlurred.alpha = blurredAlpha;
      }
    }
  }
  // Ticker-based landing animation — runs in the same PIXI ticker as spin
  // for seamless handoff (no frame gap between spin and stop)
  // Uses BackOut easing with configurable overshoot for bounce feel
  _calculateAndExecuteStop() {
    if (this.hasStopped || this.stopTweenActive) return;
    this.stopTweenActive = true;

    // DON'T remove the ticker — the stop animation runs inside it

    const hasApiData = this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    if (hasApiData) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites();
      }
    } else if (this.pendingLandingSymbolId !== null) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.symbolId = this.pendingLandingSymbolId;
        this.pendingLandingSymbolId = null;
      }
    }

    const currentDrift = this._calculateCurrentDrift();
    const baseLandingDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const adjustedLandingDistance = baseLandingDistance - currentDrift;

    const speedConfig = this._getSpeedConfig();
    const overshoot = speedConfig.BOUNCE_OVERSHOOT;

    // Landing duration: velocity-derived (scales automatically with speed/tension)
    // When tension is active, _tensionSpeedMultiplier reduces effectiveVelocity,
    // which naturally produces a longer, gentler landing — same bounce, same formula.
    // Fallback to LANDING_DURATION if explicitly set (TURBO/QUICK backwards compat)
    let stopDuration;
    const landingDuration = speedConfig.LANDING_DURATION || null;
    if (landingDuration) {
      // Legacy path: hardcoded landing duration (TURBO/QUICK)
      stopDuration = landingDuration;
    } else {
      // Velocity-matched: landing adapts to current spin speed (including tension deceleration)
      const effectiveVelocity = this.normalizedSpinSpeed
        * (this._tensionDecelerating ? this._tensionSpeedMultiplier : 1.0);
      const velocityMatchedDuration =
        ((overshoot + 3) * adjustedLandingDistance * 1000) /
        effectiveVelocity;
      stopDuration = velocityMatchedDuration * (speedConfig.STOP_SLOWDOWN_FACTOR ?? 1.0);
      // Tension landing: stretch duration to compensate for overshoot appearing faster against slow spin.
      // Per-speed override (speedConfig.TENSION_LANDING_SLOWDOWN) takes priority over global config.
      // Turbo/super_turbo set 1.0 (no slowdown) since their decelerated velocity is still fast enough.
      if (this._tensionDecelerating) {
        const tensionLandingSlowdown = speedConfig.TENSION_LANDING_SLOWDOWN
          ?? FEATURES_CONFIG.TENSION.REEL_DECELERATION.TENSION_LANDING_SLOWDOWN
          ?? 1.0;
        stopDuration *= tensionLandingSlowdown;
      }

    }

    this._setLandingSymbolOnVisibleSprites();

    // Landing symbols are above the visible area — set them to normal (non-blurred)
    // textures immediately so they're already sharp when they scroll into view
    {
      const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
      for (let i = 0; i < landingZoneEnd; i++) {
        const spriteData = this.spinningSprites[i];
        if (!spriteData) continue;
        spriteData.normalSprite.alpha = 1;
        if (spriteData.blurredSprite) spriteData.blurredSprite.alpha = 0;
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }

      this.isBlurred = false;
    }

    if (this.reels && this.reels.length > 1) {
      this.symbolId = this.reels[1];
    }

    // Store stop params for the ticker to use (replaces anime.js tween)
    this._stopElapsed = 0;
    this._stopDistance = adjustedLandingDistance;
    this._stopDuration = stopDuration;
    this._stopOvershoot = overshoot;
    this._stopLastEasedPos = 0;



    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = adjustedLandingDistance;
    this._landingPeakFired = false; // Reset peak detection for this landing
    this.isPlaying = false;
    this.isDecelerating = false;
  }

  // Shared landing finalization — called when ticker-based stop animation completes
  _finalizeLanding() {
    // Remove ticker — stop animation is done
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Clear stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isDecelerating = false;

    // Reset tension deceleration state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    const hadPendingResize = this._pendingPositionUpdate;
    if (this._pendingPositionUpdate) {
      const wasStopped = this.hasStopped;
      const wasStopTweenActive = this.stopTweenActive;
      this.stopTweenActive = false;
      this.activeStopAnimation = null;

      this._applyPendingResize();

      this.hasStopped = wasStopped;
      this.stopTweenActive = wasStopTweenActive;
    }

    // Rotate array: move last N sprites to front (single O(n) operation)
    const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE_SYMBOLS;
    const tail = this.spinningSprites.splice(cut);
    this.spinningSprites.unshift(...tail);

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Set critical state flags immediately
    this.hasStopped = true;
    this.stopTweenActive = false;
    this.spinningContainer.y = 0;
    this.lockedSpinSpeed = null;
    this.currentVelocity = 0;

    // 🚀 FIRE LANDING ANIMATIONS IMMEDIATELY - highest priority for smoothest UX
    if (this.onReelStop) {
      this.onReelStop(this.reelIndex, this.reels);
    }

    // Fire onReelLanded on the NEXT frame — gives landing animations an uncontested
    // render frame before tension operations (dim, sound, spine) kick in
    if (this.onReelLanded) {
      const callback = this.onReelLanded;
      const reelIndex = this.reelIndex;
      requestAnimationFrame(() => callback(reelIndex));
    }

    // Heavy operations AFTER landing animations start (non-blocking for animations)
    if (hadPendingResize) {
      this._forceRescaleAllTextures();
    }

    // Unblur all remaining sprites (bottom buffer etc.)
    this._switchToNormalTextures();

    this._hideBufferSprites();

    // Reel stop sound + special symbol sounds
    // reel_stop already played at peak if overshoot was present; only play special sounds here.
    const isTurboMode = isTurboLike(this.autoPlaySpeed);
    const isTurboSimultaneousLanding = isTurboMode && !this.isTensionMode;
    const isManualStop = this._wasManualStopped;
    if (isTurboSimultaneousLanding) {
      // Turbo/super_turbo: quick_stop once on first reel (all land simultaneously)
      if (this.reelIndex === 0) {
        soundManager.play(SOUND_NAME.quick_stop);
      }
    } else if (isManualStop) {
      // Manual stop: quick_stop on the first reel that was force-stopped
      if (!ReelControllerV2._manualStopSoundPlayed) {
        ReelControllerV2._manualStopSoundPlayed = true;
        soundManager.play(SOUND_NAME.quick_stop);
      }
    } else {
      // Only play reel_stop if peak didn't already fire it
      if (!this._landingPeakFired) {
        soundManager.play(SOUND_NAME.reel_stop);
      }
      const specialSound = this._getReelStopSound();
      if (specialSound && specialSound !== SOUND_NAME.reel_stop) {
        soundManager.play(specialSound);
      }
    }

    if (this.reelIndex === Settings.REEL_COUNT - 1) {
      soundManager.stop(SOUND_NAME.reel_spin);
      this.dispatch(setSpinComplete(true));
    }
  }

  // Forces re-scaling of all sprite textures
  _forceRescaleAllTextures() {
    if (!this.spinningSprites.length) return;

    this.spinningSprites.forEach((spriteData) => {
      if (spriteData.currentSymbolId !== null) {
        const info = symbolData[spriteData.currentSymbolId];
        if (info?.name) {
          const scale = this._calculateSymbolScale(
            info.name,
            this.symbolWidth,
            this.symbolHeight,
            spriteData.currentSymbolId,
          );
          if (spriteData.normalSprite) {
            spriteData.normalSprite.scale.set(scale);
          }
          if (spriteData.blurredSprite) {
            spriteData.blurredSprite.scale.set(scale);
          }
        }
      }
    });
  }

  // Set landing symbol - typically called with API data during spin
  setLandingSymbol(symbolId) {
    if (symbolId === undefined || symbolId === null) {
      return;
    }

    if (this.isPlaying && this.spinningSprites.length > 0) {
      if (this._isLandingSpriteVisible()) {
        this.pendingLandingSymbolId = symbolId;
        this.symbolId = symbolId;
      } else {
        this.symbolId = symbolId;
        this.pendingLandingSymbolId = null;
        this._setLandingSymbolOnVisibleSprites(symbolId);
      }
      return;
    }

    this.symbolId = symbolId;
    this.updateSymbolTexture(symbolId);
  }

  // Applies pending resize
  _applyPendingResize() {
    if (!this._pendingPositionUpdate) return;

    const oldSymbolHeight = this._originalSymbolHeight || this.symbolHeight;
    const newSymbolHeight = this._targetSymbolHeight;
    const scaleRatio = newSymbolHeight / oldSymbolHeight;
    const isSpinning = this.isPlaying && !this.hasStopped;
    const isStopAnimating = this.stopTweenActive && this.activeStopAnimation;

    if (isStopAnimating) {
      return;
    }

    this.symbolHeight = this._targetSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this.nativeDimensions = null;

    // Invalidate caches when symbol height changes
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear(); // Clear symbol scale cache

    if (isSpinning && Math.abs(scaleRatio - 1.0) > 0.0001) {
      this.spinningSprites.forEach((spriteData) => {
        spriteData.container.y =
          Math.round(spriteData.container.y * scaleRatio * PRECISION_FACTOR) /
          PRECISION_FACTOR;
      });

      this.lastCalculatedPosition =
        Math.round(
          this.lastCalculatedPosition * scaleRatio * PRECISION_FACTOR,
        ) / PRECISION_FACTOR;

      const speedConfig = this._getSpeedConfig();
      this.normalizedSpinSpeed =
        (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    } else {
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight,
              spriteData.currentSymbolId,
            );
            if (spriteData.normalSprite) {
              spriteData.normalSprite.scale.set(scale);
            }
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size for coin value text (both normal and blurred)
        if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize =
            this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate scaling with new dimensions (if text has content)
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const digitCount = displayValue.replace(/[^0-9]/g, "").length;

              if (digitCount <= 4) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else if (digitCount >= 5 && digitCount <= 8) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.set(scale);
              }
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;

            // Reset scale first
            spriteData.valueTextBlurred.scale.set(1);

            // Force update to get accurate measurements with new font size
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            // Recalculate scaling with new dimensions (if text has content)
            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const digitCount = displayValue.replace(/[^0-9]/g, "").length;

                if (digitCount <= 4) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else if (digitCount >= 5 && digitCount <= 8) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else {
                  const scale = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.set(scale);
                }
              }
            }
          }
        }
      });
    }

    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = this.symbolHeight;
  }

  // Update dimensions when symbol size changes
  resizeSymbol(newSymbolWidth, newSymbolHeight) {
    this._targetSymbolHeight = newSymbolHeight;
    this._targetSymbolWidth = newSymbolWidth;

    if (
      this.symbolHeight === newSymbolHeight &&
      this.symbolWidth === this._targetSymbolWidth
    ) {
      this._pendingPositionUpdate = false;
      return;
    }

    const oldSymbolHeight = this.symbolHeight;

    // If spinning OR stopping, defer update to avoid mid-animation position chaos
    if ((this.isPlaying || this.stopTweenActive) && !this.hasStopped) {
      this._originalSymbolHeight = oldSymbolHeight;
      this._pendingPositionUpdate = true;
      return;
    }

    // Not animating: update immediately
    this.symbolHeight = newSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this._originalSymbolHeight = oldSymbolHeight;
    this._pendingPositionUpdate = false;
    this.nativeDimensions = null;

    // Invalidate caches when symbol height changes
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear(); // Clear symbol scale cache

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight,
              spriteData.currentSymbolId,
            );
            if (spriteData.normalSprite) {
              spriteData.normalSprite.scale.set(scale);
            }
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size and position for coin value text (both normal and blurred)
        if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize =
            this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;
          spriteData.valueText.y = this.symbolHeight * COIN_VALUE_Y_OFFSET;

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate uniform scaling if text overflows
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const scale = maxTextWidth / actualTextWidth;
              spriteData.valueText.scale.set(scale);
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;
            spriteData.valueTextBlurred.y = this.symbolHeight * COIN_VALUE_Y_OFFSET;

            spriteData.valueTextBlurred.scale.set(1);
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueTextBlurred.scale.set(scale);
              }
            }
          }
        }
      });
    }
  }

  // Buffer Sprite Visibility — hide non-visible sprites when idle to prevent overflow bleed

  // Hides buffer sprites (above/below visible area) when reels are idle
  _hideBufferSprites() {
    if (!FEATURES_CONFIG.SPIN.HIDE_IDLE_BUFFERS) return;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i < VISIBLE_SYMBOL_START_INDEX || i >= VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT) {
        this.spinningSprites[i].container.visible = false;
      }
    }
  }

  // Shows all buffer sprites before spin starts (restores full strip for animation)
  _showBufferSprites() {
    if (!FEATURES_CONFIG.SPIN.HIDE_IDLE_BUFFERS) return;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.visible = true;
    }
  }

  // Texture Visibility Control (for overlay spine animations)

  // Hides symbol texture to show overlay spine animation
  hideSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    if (sprite.normalSprite) {
      sprite.normalSprite.alpha = 0.001;
    }
    if (sprite.blurredSprite) {
      sprite.blurredSprite.alpha = 0.001;
    }
    if (sprite.valueText) {
      sprite.valueText.alpha = 0;
    }
    if (sprite.valueTextBlurred) {
      sprite.valueTextBlurred.alpha = 0;
    }
  }

  // Shows symbol texture (hides overlay animation)
  showSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // Recalculate scale when showing sprite (fixes scaling after expanding wild animations)
    if (sprite.currentSymbolId !== null) {
      const info = symbolData[sprite.currentSymbolId];
      if (info?.name) {
        const scale = this._calculateSymbolScale(
          info.name,
          this.symbolWidth,
          this.symbolHeight,
          sprite.currentSymbolId,
        );
        if (sprite.normalSprite) {
          sprite.normalSprite.scale.set(scale);
        }
        if (sprite.blurredSprite) {
          sprite.blurredSprite.scale.set(scale);
        }
      }
    }

    if (sprite.normalSprite) {
      sprite.normalSprite.alpha = 1;
    }
    if (sprite.blurredSprite) {
      sprite.blurredSprite.alpha = 0.001;
    }
    if (sprite.valueText && sprite.valueText.text !== "") {
      sprite.valueText.alpha = 1;
    }
    if (sprite.valueTextBlurred) {
      sprite.valueTextBlurred.alpha = 0;
    }
  }

  // Shows all visible symbol textures (clears overlay animations)
  showAllSymbolTextures() {
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (sprite) {
        // Recalculate scale when showing sprite (fixes scaling after expanding wild animations)
        if (sprite.currentSymbolId !== null) {
          const info = symbolData[sprite.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight,
              sprite.currentSymbolId,
            );
            if (sprite.normalSprite) {
              sprite.normalSprite.scale.set(scale);
            }
            if (sprite.blurredSprite) {
              sprite.blurredSprite.scale.set(scale);
            }
          }
        }

        if (sprite.normalSprite) {
          sprite.normalSprite.alpha = 1;
        }
        if (sprite.blurredSprite) {
          sprite.blurredSprite.alpha = 0.001;
        }
        if (sprite.valueText && sprite.valueText.text !== "") {
          sprite.valueText.alpha = 1;
        }
        if (sprite.valueTextBlurred) {
          sprite.valueTextBlurred.alpha = 0;
        }
      }
    }
  }

  // Exposes sprites in format compatible with useWinController - returns only visible sprites
  get symbolsContainer() {
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    const visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);

    return {
      children: visibleSprites.map((sprite, overlayIndex) => ({
        currentSymbolId: sprite.currentSymbolId,
        symbolId: sprite.currentSymbolId,
        overlayIndex: overlayIndex, // Direct mapping: 0, 1, 2, 3
      })),
    };
  }

  // Expanding Wild Support

  // Replaces all visible symbols with target symbol (e.g., wild expansion)
  // @param {number} targetSymbolId - Symbol ID to replace with
  // @param {boolean} hideTextures - If true, hides textures (for cascade mode). If false, keeps visible (for full-reel mode)
  replaceVisibleSymbols(targetSymbolId, hideTextures = true) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    const textureName = symbolInfo.texture;
    const texture = this.spritesheet?.textures?.[`${textureName}.png`];
    if (!texture) return;

    // Replace all visible sprites
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) continue;

      sprite.normalSprite.texture = texture;
      sprite.currentSymbolId = targetSymbolId;

      // Only hide textures if requested (cascade mode needs this, full-reel mode doesn't)
      if (hideTextures) {
        sprite.normalSprite.alpha = 0.001;
        sprite.blurredSprite.alpha = 0.001;
      }
    }
  }

  // Replaces single symbol at position (for cascading wild animation)
  replaceSymbolAtPosition(symbolIndex, targetSymbolId) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    // CRITICAL: Update the reels array for this position
    // This prevents blinking when next spin starts
    // Create a new array since the original might be read-only (from Redux state)
    if (this.reels && this.reels.length > symbolIndex) {
      this.reels = this.reels.map((id, idx) =>
        idx === symbolIndex ? targetSymbolId : id,
      );
    }

    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // Force update by temporarily clearing currentSymbolId to bypass early exit in _setSpriteTexture
    sprite.currentSymbolId = null;

    // Use _setSpriteTexture to properly update ALL textures (normal, blurred, coin values)
    // This ensures when showSymbolTexture() is called later, it reveals the correct wild texture
    this._setSpriteTexture(sprite, targetSymbolId);

    // Then hide the textures for the animation overlay to show
    sprite.normalSprite.alpha = 0.001;
    sprite.blurredSprite.alpha = 0.001;
  }

  // Cleanup

  clearAllTimeouts() {
    // Stop and clear all anime.js animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this.spinningContainer); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
    }

    if (this.activeStopAnimation) {
      this.activeStopAnimation.pause();
      anime.remove(this.activeStopAnimation); // Force-eject from anime.js internal tracker
      this.activeStopAnimation = null;
    }

    // Remove PIXI.Ticker callback
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }
  }

  _cleanupSpinningSprites() {
    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((symbolData) => {
        try {
          if (symbolData.container?.parent && this.spinningContainer) {
            this.spinningContainer.removeChild(symbolData.container);
          }
          if (symbolData.normalSprite && !symbolData.normalSprite.destroyed) {
            symbolData.normalSprite.destroy();
          }
          if (symbolData.blurredSprite && !symbolData.blurredSprite.destroyed) {
            symbolData.blurredSprite.destroy();
          }
          if (symbolData.valueText && !symbolData.valueText.destroyed) {
            symbolData.valueText.destroy();
          }
          if (
            symbolData.valueTextBlurred &&
            !symbolData.valueTextBlurred.destroyed
          ) {
            symbolData.valueTextBlurred.destroy();
          }
          if (symbolData.container && !symbolData.container.destroyed) {
            symbolData.container.destroy();
          }
        } catch (e) {
          // ignore
        }
      });
      this.spinningSprites = [];
    }

    this.isPlaying = false;
    this.hasStopped = false;
  }
}

// Static flag: ensures quick_stop plays only once across all reels during manual stop
ReelControllerV2._manualStopSoundPlayed = false;

export default ReelControllerV2;
